import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { GetPackageDetailsRequest } from 'ts/package/classes/GetPackageDetailsRequest';
import { getPackageDetails } from 'services/package';
import { PackageDetailsResponse } from 'ts/package/interfaces/PackageDetails';
import AdditionalDetails from './AdditionalDetails';
import SummaryDetails from './SummaryDetails';
import DetailsPackageSkeleton from './DetailsPackageSkeleton';
import TourDetails from './TourDetails';
import ItineraryDetails from './ItineraryDetails';
import RoomsDetails from './RoomDetails';
import TicketDetails from './TicketDetailsOptions';
import ExpiredToken from 'components/shared/ExpiredToken';
import * as idb from 'utils/shared/idbUrl';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { openInNewWindow, saveScreenshot } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import { RoomTour } from 'ts/package/interfaces/Package';
import { faArrowCircleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import useDataLayer from 'hooks/usePackageDataLayer';
import { MainContext } from 'contexts/MainContext';

const DetailsTourPage: FC = () => {
  const [urlParams] = useSearchParams();
  const [response, setResponse] = useState<PackageDetailsResponse>();
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalAvgPrice, setTotalAvgPrice] = useState(0);
  const detailsRef = useRef<HTMLDivElement>(null);
  const [availableRooms, setAvailableRooms] = useState<RoomTour[]>([]);
  const [infants, setInfants] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userSiteConfig } = useContext(MainContext);

  useGetPage('ILPackages/Details');
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  const { onClickBack, onClickSearch } = usePageInMemory(idbKey, () => saveState());
  const stateRef = useRef<any>();
  const { pushPackageProductDetails } = useDataLayer(true);
  stateRef.current = {
    response,
    availableRooms,
    infants,
    totalPrice,
    totalAvgPrice,
  };

  useEffect(() => {
    (async () => {
      const savedState = await idb.getPageState(idbKey);
      if (savedState && savedState.response) {
        setResponse(savedState.response);
        setAvailableRooms(savedState.availableRooms);
        setInfants(savedState.infants);
        setTotalPrice(savedState.totalPrice);
        setTotalAvgPrice(savedState.totalAvgPrice);

        idb.deletePageState(idbKey);
      } else {
        const params = Object.fromEntries(urlParams);
        const body = new GetPackageDetailsRequest(params);
        const response = await getPackageDetails(body);

        setResponse(response);

        if (response?.dealModel) {
          pushPackageProductDetails(response?.dealModel);
        }
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangePax = (totalPrice: number, totalAvgPrice: number) => {
    setTotalPrice(totalPrice);
    setTotalAvgPrice(totalAvgPrice);
  };

  const onClickBook = () => {
    const room = availableRooms.find((item) => item.checked);
    urlParams.append('selectedRooms', `${room?.bookCode}-1`);
    urlParams.append('searchToken', response?.searchToken || '');
    if (infants) {
      urlParams.append('infants', infants.toString());
    }

    saveScreenshot(detailsRef.current, 'Details', 'Packages');

    if (userSiteConfig.base.openDetailsInNewTab) {
      openInNewWindow('/tour/book?' + new URLSearchParams(urlParams).toString());
    } else {
      saveState();
      navigate({ pathname: '/tour/book', search: urlParams.toString() });
    }
  };
  const saveState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  return (
    <div className="details-wrapper package-details-wrapper" ref={detailsRef}>
      {!!response && !response?.hasErrors && !response.sessionExpired && (
        <Fragment>
          <div className="details package-details">
            {window.history.length > 1 && (
              <div className="mb-20 default-shadow flex-between dyn-change-header">
                <Button
                  title={t('back')}
                  icon={faArrowCircleLeft}
                  onClick={() => onClickBack('tour', 'results')}
                  className="btn btn-link"
                />
                <Button
                  title={t('search.new')}
                  icon={faSearch}
                  onClick={() => onClickSearch('tour', '/tourssimple')}
                  className="btn btn-link"
                />
              </div>
            )}
            <TourDetails
              dealModel={response.dealModel}
              totalAvgPrice={totalAvgPrice}
              totalPrice={totalPrice}
            />

            {!!response.dealModel?.options.length &&
              !!response.dealModel?.options[0].flights?.length && (
                <Fragment>
                  <TicketDetails
                    type="departure"
                    bound={response.dealModel?.options[0].flights[0].outbound}
                  />
                  <TicketDetails
                    type="return"
                    bound={response.dealModel?.options[0].flights[0].inbound}
                  />
                </Fragment>
              )}
            <AdditionalDetails dealModel={response.dealModel} />

            <ItineraryDetails dealModel={response.dealModel} />
            <RoomsDetails
              onChangePax={onChangePax}
              dealModel={response.dealModel}
              onClickBook={onClickBook}
              availableRooms={availableRooms}
              setAvailableRooms={setAvailableRooms}
              infants={infants}
              setInfants={setInfants}
            />
          </div>
          <SummaryDetails
            recommended={response.recommendedDeals}
            mapUrl={response.googleMapOfAirports}
          />
        </Fragment>
      )}

      {response?.sessionExpired && <ExpiredToken storePath="tourssimple" />}

      {!response && <DetailsPackageSkeleton />}
    </div>
  );
};

export default withErrorBoundary(DetailsTourPage, 'DetailsTourPage');
